import { cn } from '@quadralize/utils'
import type * as React from 'react'
import { FULL_PCT, MINS_PER_HOUR, OCCUPANCY_HIGH, OCCUPANCY_MED } from './constants'
import type { SchedulerDensity, SchedulerEvent, SchedulerResource } from './types'
import { toEndMin, toMin } from './utils'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ResourceTabsProps {
  resources: SchedulerResource[]
  activeId: string
  events: SchedulerEvent[]
  showOccupancyIndicators?: boolean
  density?: SchedulerDensity
  startTime: number
  endTime: number
  onSelect: (id: string) => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ResourceTabs = ({
  resources,
  activeId,
  events,
  showOccupancyIndicators = true,
  density = 'comfortable',
  startTime,
  endTime,
  onSelect,
}: ResourceTabsProps): React.JSX.Element => {
  const total = (endTime - startTime) * MINS_PER_HOUR
  const isCompact = density === 'compact'
  const shouldStretchTabs = resources.length <= 2

  return (
    <div
      className={cn(
        'w-full min-w-0 border-b border-border-default bg-bg-subtle/90 shrink-0 supports-backdrop-filter:backdrop-blur-md',
        'px-0 py-0',
      )}
    >
      <div
        className={cn(
          'flex items-stretch gap-0 overflow-x-auto snap-x snap-mandatory',
          '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {resources.map((r) => {
          const resEvents = events.filter((e) => e.resourceId === r.id)
          const isActive = r.id === activeId
          // Simple occupancy: sum of booked minutes / total minutes
          const booked = resEvents.reduce(
            (acc, e) => acc + (toEndMin(e.startTime, e.endTime) - toMin(e.startTime)),
            0,
          )
          const occ = total ? Math.min(Math.round((booked / total) * FULL_PCT), FULL_PCT) : 0

          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onSelect(r.id)}
              aria-pressed={isActive}
              className={cn(
                'flex min-w-0 flex-col items-center rounded-none relative transition-colors border-r border-border-default snap-start',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                isCompact ? 'gap-1 px-2 py-1.5' : 'gap-1.5 px-2.5 py-2',
                shouldStretchTabs ? 'flex-1' : cn('shrink-0', isCompact ? 'min-w-30' : 'min-w-34'),
                isActive
                  ? 'border-primary/35 text-primary bg-primary/12'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-muted/60',
              )}
            >
              <div className="flex items-center gap-1.5 max-w-full">
                {r.icon && (
                  <span
                    className={cn(
                      'transition-colors shrink-0',
                      isActive ? 'text-primary' : 'text-text-tertiary',
                    )}
                  >
                    {r.icon}
                  </span>
                )}
                <span className="text-[11px] font-semibold truncate">{r.name}</span>
                {showOccupancyIndicators && (
                  <span
                    className={cn(
                      'tabular-nums text-text-tertiary',
                      isCompact ? 'text-[9px]' : 'text-[10px]',
                    )}
                  >
                    {occ}%
                  </span>
                )}
              </div>
              {showOccupancyIndicators && (
                <div
                  className={cn(
                    'w-full rounded-full bg-border-subtle overflow-hidden',
                    isCompact ? 'h-0.5' : 'h-1',
                  )}
                >
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-500',
                      occ >= OCCUPANCY_HIGH
                        ? 'bg-error'
                        : occ >= OCCUPANCY_MED
                          ? 'bg-warning'
                          : 'bg-success',
                    )}
                    style={{ width: `${occ}%` }}
                  />
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

ResourceTabs.displayName = 'ResourceTabs'
